import { onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'

const AUTO_OPEN_KEY = 'authModalAutoOpened'

function hasAutoOpenFlag () {
  try {
    const val = typeof window !== 'undefined' && window.localStorage.getItem(AUTO_OPEN_KEY) === '1'
    return val
  } catch (_) {
    // Be safe: if storage not available, treat as set to avoid annoyance
    return true
  }
}

function isIosNative () {
  try {
    return typeof window !== 'undefined' &&
           window.Capacitor &&
           window.Capacitor.isNativePlatform() &&
           window.Capacitor.getPlatform() === 'ios'
  } catch (_) {
    return false
  }
}

function setAutoOpenFlag () {
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(AUTO_OPEN_KEY, '1')
    }
  } catch (_) {
    // ignore
  }
}

const EXCLUDED_ROUTES = ['Intro', 'IntroAnimation', 'Login', 'Register', 'ForgotPassword', 'ResetPassword', 'VerifyEmail', 'GoogleCallback', 'EmailVerificationPending', 'RegistrationConfirmation', 'FreeMealPlan', 'Planner']

export function useAuthModalAutoOpen ({ showAuth, isAuthenticated, openAuthModal, initialTab = 'login' }) {
  const route = useRoute()

  let timerId = null
  let clickCount = 0
  let hasSignal = false
  let routeStop = null
  let listenersActive = false
  let initialized = false
  let timeElapsed = false
  let pendingOpenTimer = null

  function cleanupAutoOpenTracking () {
    if (timerId) {
      clearTimeout(timerId)
      timerId = null
    }
    if (pendingOpenTimer) {
      clearTimeout(pendingOpenTimer)
      pendingOpenTimer = null
    }
    if (listenersActive) {
      window.removeEventListener('scroll', onScrollSignal, { passive: true })
      document.removeEventListener('click', onClickSignal, true)
      listenersActive = false
    }
    if (routeStop) {
      routeStop()
      routeStop = null
    }
  }

  function stopSignalTrackingOnly () {
    if (listenersActive) {
      window.removeEventListener('scroll', onScrollSignal, { passive: true })
      document.removeEventListener('click', onClickSignal, true)
      listenersActive = false
    }
    if (routeStop) {
      routeStop()
      routeStop = null
    }
  }

  function conditionsAllowAutoOpen () {
    if (isIosNative()) return false
    if (hasAutoOpenFlag()) return false
    if (isAuthenticated?.value) return false
    if (showAuth.value) return false
    if (!timeElapsed) return false
    if (!hasSignal) return false
    if (EXCLUDED_ROUTES.includes(route.name)) return false
    // Paid entry pages opt out by meta rather than by name, so a new campaign
    // landing page cannot silently inherit a sign-up modal over its first view.
    if (route.meta?.suppressAuthAutoOpen) return false
    return true
  }

  function maybeAutoOpen () {
    if (!conditionsAllowAutoOpen()) return
    openAuthModal('', initialTab)
    setAutoOpenFlag()
    cleanupAutoOpenTracking()
  }

  function onScrollSignal () {
    if (hasSignal) return
    const doc = document.documentElement
    const total = Math.max(doc.scrollHeight, (document.body)?.scrollHeight || 0)
    const viewportBottom = window.scrollY + window.innerHeight
    const ratio = total > 0 ? viewportBottom / total : 0
    if (total > 0 && viewportBottom / total >= 0.8) {
      hasSignal = true
      // Stop listening for other signals, but keep the 30s gate timer running
      stopSignalTrackingOnly()
      if (timeElapsed) {
        if (pendingOpenTimer) clearTimeout(pendingOpenTimer)
        pendingOpenTimer = setTimeout(() => {
          maybeAutoOpen()
        }, 3000)
      } // else: wait for 30s timer to elapse, which will call maybeAutoOpen
    }
  }

  function onClickSignal () {
    if (hasSignal) return
    clickCount += 1
    if (clickCount >= 2) {
      hasSignal = true
      stopSignalTrackingOnly()
      if (timeElapsed) {
        if (pendingOpenTimer) clearTimeout(pendingOpenTimer)
        pendingOpenTimer = setTimeout(() => {
          maybeAutoOpen()
        }, 3000)
      }
    }
  }

  function startSignalTracking () {
    if (listenersActive || hasSignal) return
    window.addEventListener('scroll', onScrollSignal, { passive: true })
    document.addEventListener('click', onClickSignal, true)
    listenersActive = true
    const initialPath = route.fullPath
    routeStop = watch(() => route.fullPath, (newPath) => {
      if (!hasSignal && newPath !== initialPath) {
        hasSignal = true
        stopSignalTrackingOnly()
        if (timeElapsed) {
          if (pendingOpenTimer) clearTimeout(pendingOpenTimer)
          pendingOpenTimer = setTimeout(() => {
            maybeAutoOpen()
          }, 3000)
        }
      }
    }, { flush: 'post' })
  }

  function startThirtySecondTimer () {
    if (timerId) return
    timerId = setTimeout(() => {
      timeElapsed = true
      maybeAutoOpen()
    }, 30000)
  }

  onMounted(() => {
    if (initialized) return
    initialized = true
    if (isIosNative()) return
    if (hasAutoOpenFlag()) return
    if (isAuthenticated?.value) return
    startSignalTracking()
    startThirtySecondTimer()
  })

  onUnmounted(() => {
    cleanupAutoOpenTracking()
  })

  // If user becomes authenticated at runtime, stop tracking
  watch(() => isAuthenticated?.value, (authed) => {
    if (authed) {
      cleanupAutoOpenTracking()
    }
  })

  // Re-check conditions on route change (e.g. moving from an excluded route to an allowed one)
  watch(() => route.name, () => {
    maybeAutoOpen()
  })

  // "Only once" rule: if the user closes the modal manually, set the flag
  let prevShow = showAuth.value
  watch(showAuth, (now) => {
    if (prevShow && !now) {
      if (!hasAutoOpenFlag()) setAutoOpenFlag()
      cleanupAutoOpenTracking()
    }
    prevShow = now
  })

  return {
    setAutoOpenFlag, hasAutoOpenFlag, cleanupAutoOpenTracking
  }
}

export default useAuthModalAutoOpen
